import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  CONFIG_CODES,
  DB_SYNC_DELAY,
} from 'constants/settings/internalConfig';
import { ProcessorWebhookConfig } from 'types/settings/internalConfig';
import { PAYMENT_CONFIG } from 'state/paymentConfig/type';

interface UseWebhookActionsProps {
  cardReaderValues: Array<PAYMENT_CONFIG.IGetCardReaderValues> | undefined;
  processorConfigs: ProcessorWebhookConfig[];
  updateGlobalCardReaderValues: (value: PAYMENT_CONFIG.IGlobalCardReader) => Promise<void>;
  saveGlobalCardReaderValues: (value: PAYMENT_CONFIG.IGlobalCardReader) => Promise<void>;
  toFetchCardReaderValues: (
    processorId?: string
  ) => Promise<PAYMENT_CONFIG.IGetCardReaderList | undefined>;
}

export const useWebhookActions = ({
  cardReaderValues,
  processorConfigs,
  updateGlobalCardReaderValues,
  saveGlobalCardReaderValues,
  toFetchCardReaderValues,
}: UseWebhookActionsProps) => {
  const handleToggleChange = useCallback(
    async (processorId: string, value: boolean) => {
      const existingConfig = cardReaderValues?.find(
        (config) =>
          config.code === CONFIG_CODES.BYPASS_WEBHOOK_CALLBACK &&
          config.processorId === processorId
      );

      const processorName =
        processorConfigs.find((p) => p.processorId === processorId)?.processorName || '';

      if (!existingConfig) {
        await saveGlobalCardReaderValues({
          name: `${processorName}_${CONFIG_CODES.BYPASS_WEBHOOK_CALLBACK}`,
          category: null,
          code: CONFIG_CODES.BYPASS_WEBHOOK_CALLBACK,
          value: value.toString(),
          isSecret: false,
          isRequired: false,
          id: uuidv4(),
          processorId,
        });
      } else {
        await updateGlobalCardReaderValues({
          name: existingConfig.name,
          category: existingConfig.category,
          code: existingConfig.code,
          value: value.toString(),
          isSecret: existingConfig.isSecret,
          isRequired: existingConfig.isRequired,
          id: existingConfig.id,
          processorId: existingConfig.processorId,
        });
      }

      await new Promise((resolve) => setTimeout(resolve, DB_SYNC_DELAY));
      await toFetchCardReaderValues(processorId);
    },
    [
      cardReaderValues,
      processorConfigs,
      saveGlobalCardReaderValues,
      updateGlobalCardReaderValues,
      toFetchCardReaderValues,
    ]
  );

  const handleWebhookStatusChange = useCallback(
    async (processorId: string, status: string) => {
      const existingConfig = cardReaderValues?.find(
        (config) =>
          config.code === CONFIG_CODES.WEBHOOK_BYPASS_STATUS &&
          config.processorId === processorId
      );

      const processorName =
        processorConfigs.find((p) => p.processorId === processorId)?.processorName || '';

      if (!existingConfig) {
        await saveGlobalCardReaderValues({
          name: `${processorName}_${CONFIG_CODES.WEBHOOK_BYPASS_STATUS}`,
          category: null,
          code: CONFIG_CODES.WEBHOOK_BYPASS_STATUS,
          value: status,
          isSecret: false,
          isRequired: false,
          id: uuidv4(),
          processorId,
        });
      } else {
        await updateGlobalCardReaderValues({
          name: existingConfig.name,
          category: existingConfig.category,
          code: existingConfig.code,
          value: status,
          isSecret: existingConfig.isSecret,
          isRequired: existingConfig.isRequired,
          id: existingConfig.id,
          processorId: existingConfig.processorId,
        });
      }

      await new Promise((resolve) => setTimeout(resolve, DB_SYNC_DELAY));
      await toFetchCardReaderValues(processorId);
    },
    [
      cardReaderValues,
      processorConfigs,
      saveGlobalCardReaderValues,
      updateGlobalCardReaderValues,
      toFetchCardReaderValues,
    ]
  );

  return {
    handleToggleChange,
    handleWebhookStatusChange,
  };
};
